/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * CartWarningModal.jsx
 * Created by Michael Bray on 2/14/18.
 * Updated by Aaron Luna on 6/30/22.
 */

import * as React from 'react';
import { differenceInMilliseconds, intervalToDuration } from 'date-fns';
import PropTypes from 'prop-types';

import {
    Button,
    ButtonGroup,
    ModalActions,
    StyledModal,
    announce,
    pluralize,
    SecurityHelper,
} from 'sarsaparilla';

const { isWidgetSession, getWidgetHomeUrl } = SecurityHelper;

const propTypes = {
    extendTimer: PropTypes.func.isRequired,
    fetchCart: PropTypes.func.isRequired,
    expiration: PropTypes.string,
    expiresIn: PropTypes.number,
    onFinalCartWarningModal: PropTypes.func.isRequired,
    sessionWarning: PropTypes.bool,
    domainsTrustedOrigins: PropTypes.array,
    isExtendingTime: PropTypes.bool,
};

const defaultProps = {
    expiration: '',
    expiresIn: 0,
    sessionWarning: false,
    domainsTrustedOrigins: [
        'http://localhost:9900',
        'https://www.r1s-dev.com',
        'https://www.r1s-test.com',
        // eslint-disable-next-line no-restricted-syntax
        'https://www.recreation.gov',
        'https://cdn.r1s-dev.com',
        'https://cdn.r1s-test.com',
        // eslint-disable-next-line no-restricted-syntax
        'https://cdn.recreation.gov',
        'https://www.nps.gov',
        'https://www.blm.gov',
        'https://www.fs.usda.gov',
    ],
};

interface MessageEvents {
    [key: string]: {
        type: string;
        description: string;
    };
}

// events to notify changes to the widget

export const CONTINUE_SHOPPING_EVENT = 'CONTINUE_SHOPPING_EVENT';

export const MESSAGE_EVENTS: MessageEvents = {
    [CONTINUE_SHOPPING_EVENT]: {
        type: CONTINUE_SHOPPING_EVENT,
        description: 'continue shopping event',
    },
};

export interface CartWarningModalProps {
    extendTimer: () => void;
    fetchCart: (flag?: boolean) => void;
    expiration: string;
    expiresIn: number;
    onFinalCartWarningModal: () => void;
    sessionWarning?: boolean;
    domainsTrustedOrigins?: string[];
    isExtendingTime?: boolean;
}

interface CartWarningModalState {
    timer: ReturnType<typeof setInterval> | undefined;
    modalOpen: boolean;
    expired: boolean;
    fetchingCart: boolean;
    lastFetchTime: Date | undefined;
    ignored: boolean;
}

export default class CartWarningModal extends React.Component<
    CartWarningModalProps,
    CartWarningModalState
> {
    static propTypes = propTypes;

    static defaultProps = defaultProps;

    isStillMounted: boolean | undefined;

    timeRemainingRef: { current: number };

    constructor(props: CartWarningModalProps) {
        super(props);

        const ignored = window.sessionStorage.getItem('r1s_cart_ignored') === 'true';

        this.state = {
            timer: undefined,
            modalOpen: false,
            expired: false,
            fetchingCart: false,
            lastFetchTime: undefined,
            ignored,
        };

        /**
         * Swapping Rect.createRef() for this, because React.createRef() returns
         * the type React.RefObject which is not mutable
         */
        this.timeRemainingRef = {
            current: 0,
        };
    }

    componentDidMount() {
        this.isStillMounted = true;
        const timer = setInterval(() => this.tick(), 1000);
        this.setState({
            timer,
        });
        this.timeRemainingRef.current =
            -1 * differenceInMilliseconds(new Date(), this.props.expiration);
    }

    componentWillUnmount() {
        this.isStillMounted = false;
        clearInterval(this.state.timer);
    }

    handlePlanYourTrip = () => {
        this.notifyEvent(CONTINUE_SHOPPING_EVENT);
        let continueShoppingUrl = '/';

        if (isWidgetSession()) {
            continueShoppingUrl = getWidgetHomeUrl() ?? '/';
        }

        window.location.assign(continueShoppingUrl);
    };

    tick = () => {
        if (this.state.fetchingCart) {
            return;
        }
        this.timeRemainingRef.current =
            -1 * differenceInMilliseconds(new Date(), this.props.expiration);
        if (this.timeRemainingRef.current > 0) {
            this.openTimeRemainingModalIfNeeded();
            return;
        }
        if (this.props.expiresIn <= 0 || this.timeRemainingRef.current <= 0) {
            this.showExpiredModal();
            return;
        }
        if (this.state.lastFetchTime) {
            // then the expiresIn is recent so use that to wait before fetching again
            const secondsSinceLastFetch =
                differenceInMilliseconds(new Date(), this.state.lastFetchTime) / 1000;
            if (secondsSinceLastFetch < this.props.expiresIn) {
                // wait before fetching again
                this.openTimeRemainingModalIfNeeded();
                return;
            }
        }

        this.fetchCartHandling();
    };

    openTimeRemainingModalIfNeeded = () => {
        if (
            this.timeRemainingRef.current <= 300000 &&
            !this.state.ignored &&
            !this.props.sessionWarning &&
            !this.props.isExtendingTime // true when extendTimer api call is inflight
        ) {
            this.setState({ modalOpen: true });
        }
    };

    fetchCartHandling = () => {
        // fetch the cart again to refresh expiresIn
        this.setState({ fetchingCart: true }, async () => {
            await this.props.fetchCart(true);
            if (this.isStillMounted) {
                this.setState({ fetchingCart: false, lastFetchTime: new Date() });
            } else {
                // only calling if no longer mounted (as it triggers a re-render)
                this.props.onFinalCartWarningModal();
            }
        });
    };

    formatTime = (num: number) => {
        if (num <= 0) {
            return '00';
        }
        return num < 10 ? `0${num}` : num.toString();
    };

    closeModal = () => {
        this.setState({
            modalOpen: false,
            ignored: true,
        });
        window.sessionStorage.setItem('r1s_cart_ignored', 'true');
        this.props.fetchCart();
    };

    extendTimer = async () => {
        if (!this.state.fetchingCart) {
            announce('You have extended your cart timer by five minutes.');
            await this.props.extendTimer();
            await this.fetchCartHandling();
            this.setState({
                modalOpen: false,
                ignored: false,
            });
            window.sessionStorage.removeItem('r1s_cart_ignored');
        }
    };

    notifyEvent = (type: string) => {
        const messageEvent = !type ? null : MESSAGE_EVENTS[type];

        if (!window.parent || !messageEvent) return;

        const origin =
            window.location !== window.parent.location
                ? document?.referrer
                : document?.location?.href;

        const trustedDomains = this.props.domainsTrustedOrigins ?? [];

        const trustedOrigin = trustedDomains.find((localOrigin) =>
            origin.includes(localOrigin)
        );

        if (trustedOrigin) window.parent.postMessage(messageEvent, trustedOrigin);
    };

    showExpiredModal = () => {
        clearInterval(this.state.timer);
        this.setState({
            modalOpen: true,
            expired: true,
        });
        window.sessionStorage.removeItem('r1s_cart_ignored');
    };

    renderTime = () => {
        const duration = intervalToDuration({
            start: new Date(),
            end: this.props.expiration,
        });

        const mm = this.formatTime(duration.minutes || 0);
        const ss = this.formatTime(duration.seconds || 0);

        const minuteText = (
            <span className="rec-sr-only">
                {pluralize(Number(mm), 'minute', 'minutes')}
            </span>
        );

        const secondText = (
            <span className="rec-sr-only">
                {pluralize(Number(ss), 'second', 'seconds')}
            </span>
        );

        return (
            <>
                {mm}
                {minuteText}
                <span aria-hidden="true">:</span>
                {ss}
                {secondText}
            </>
        );
    };

    renderContent = () => {
        if (this.state.expired) {
            return (
                <div>
                    <div className="rec-modal-content-wrap">
                        <div>
                            <p>
                                The time ran out on your cart, and items have been
                                removed. The good news is that we&#39;re still here, so
                                let&#39;s retrace your steps or take a look for new
                                adventures.
                            </p>
                        </div>
                    </div>
                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                title="Plan Your Trip"
                                aria-label="Plan Your Trip"
                                onClick={this.handlePlanYourTrip}
                                gaTrackingId="016540857659"
                            >
                                Plan Your Trip
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </div>
            );
        }

        return (
            <div>
                <div className="rec-modal-content-wrap">
                    <p>
                        This is a friendly reminder that your cart will time out in{' '}
                        <b>{this.renderTime()}</b>. Once time is up, all items will be
                        removed from your cart.
                    </p>
                    <p>
                        We can provide more time to finish this reservation, though! Would
                        five more minutes be helpful?
                    </p>
                </div>
                <ModalActions>
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button
                            title="Add Five Minutes"
                            aria-label="Add Five Minutes"
                            onClick={this.extendTimer}
                            gaTrackingId="303011773358"
                        >
                            Add Five Minutes
                        </Button>
                        <Button
                            appearance="tertiary"
                            title="Ignore"
                            aria-label="Ignore"
                            onClick={this.closeModal}
                            gaTrackingId="920807948123"
                        >
                            Ignore
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </div>
        );
    };

    render() {
        return (
            <StyledModal
                isOpen={this.state.modalOpen}
                heading={
                    this.state.expired
                        ? 'Your cart has expired.'
                        : 'Your cart is about to expire.'
                }
                shouldShowCloseButton={false}
            >
                {this.renderContent()}
            </StyledModal>
        );
    }
}
